import FW from '../fw/fw';
import Logger from '../fw/logger';


export default class Accessible {

  #rmp;
  #elementsFocus = [];
  #bindKeyboardFn = null;
  #removeFocusToContainerFn = null;
  #addTabKeyDetectionFn = null;
  #focusOnOpenOverlayTimeoutFn = null;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  #addFocus(element) {
    if (element) {
      element.classList.add('rmp-focus');
    }
    if (!this.#rmp.forceResetTimer) {
      this.#rmp.forceResetTimer = true;
    }
  }

  #removeFocus(element) {
    if (element) {
      element.classList.remove('rmp-focus');
    }
    if (this.#rmp.forceResetTimer) {
      this.#rmp.forceResetTimer = false;
    }
  }

  // keyboard
  #addTabKeyDetection(event) {
    if (event && typeof event.key === 'string' && event.key.toLowerCase() === 'tab') {
      const activeElement = document.activeElement;
      // remove focus
      for (let i = 0, len = this.#elementsFocus.length; i < len; i++) {
        this.#removeFocus(this.#elementsFocus[i]);
      }
      // add focus
      for (let i = 0, len = this.#elementsFocus.length; i < len; i++) {
        if (this.#elementsFocus[i] && activeElement === this.#elementsFocus[i]) {
          if (activeElement === this.#rmp.dom.volume) {
            FW.show(this.#rmp.dom.volumeBar);
          } else if ([
            this.#rmp.dom.seekBar,
            this.#rmp.dom.playPause,
            this.#rmp.dom.quickForward,
            this.#rmp.dom.quickRewind
          ].includes(activeElement)) {
            FW.hide(this.#rmp.dom.volumeBar);
          }
          this.#addFocus(this.#elementsFocus[i]);
          break;
        }
      }
    }
  }

  #playOrPause() {
    let inPause;
    if (this.#rmp.linearAdOnStage) {
      inPause = this.#rmp.adPaused;
    } else {
      inPause = this.#rmp.paused;
    }
    if (inPause) {
      this.#rmp.play().catch(error => {
        this.#rmp.rmpUtils.warning(1014, error);
      });
    } else {
      this.#rmp.pause();
    }
  }

  #seekToKeyboard(newCurrentTime, currentTime, duration) {
    if (this.#rmp.isVod && !this.#rmp.hideSeekBar && currentTime > -1 && duration > -1) {
      this.#rmp.seekTo(newCurrentTime).catch(error => {
        this.#rmp.rmpUtils.warning(1016, error);
      });
    }
  }

  #bindKeyboard(event) {
    if (!event || typeof event.key !== 'string' || event.key === '') {
      return;
    }
    Logger.print('accessibility', 'key is: ' + event.key);
    Logger.print('accessibility', 'element is: ', event.target);
    let currentTime = this.#rmp.currentTime;
    let duration = this.#rmp.duration;
    if (this.#rmp.isLive) {
      duration = -1;
      currentTime = -1;
    }
    const currentVolume = this.#rmp.volume;
    this.#rmp.rmpUIManagement.showChrome();
    let newVolume, newTrackId = 0;
    const audioTracks = this.#rmp.audioTracks;
    const activeTrack = audioTracks.find(track => {
      return track.active;
    });
    let rewindValue = 10000;
    let forwardValue = 10000;
    switch (event.key) {
      case ' ': // space = play/pause
        this.#playOrPause();
        break;
      case 'a': // a = audio
        if (activeTrack) {
          if (activeTrack.id !== audioTracks.length - 1) {
            newTrackId = activeTrack.id + 1;
          }
          this.#rmp.audioTrack = newTrackId;
        }
        break;
      case 'k': // k = play/pause
        this.#playOrPause();
        break;
      case 'j': // j = -10sec
        // when adUI is on - do not seek
        if (this.#rmp.hasAdUI) {
          break;
        }
        if (this.#rmp.quickRewind) {
          rewindValue = this.#rmp.quickRewind * 1000;
        }
        this.#seekToKeyboard(currentTime - rewindValue, currentTime, duration);
        break;
      case 'l': // l = +10sec
        if (this.#rmp.hasAdUI) {
          break;
        }
        if (this.#rmp.quickForward) {
          forwardValue = this.#rmp.quickForward * 1000;
        }
        this.#seekToKeyboard(currentTime + forwardValue, currentTime, duration);
        break;
      case 'f': // f = fullscreen
        if (!this.#rmp.hideFullscreen && !this.#rmp.googleCastConnected && !this.#rmp.audioOnly) {
          this.#rmp.rmpFullscreenManagement.executeFullscreenRequest();
        }
        break;
      case 'c': { // c = captions 
        if (this.#rmp.dom.captionsOverlayLevelsArea) {
          const ccButtons = this.#rmp.dom.captionsOverlayLevelsArea.querySelectorAll('.rmp-overlay-level');
          if (ccButtons.length > 0) {
            let nextIndexToActivate = 0;
            for (let i = 0, len = ccButtons.length; i < len; i++) {
              const button = ccButtons[i];
              if (button.classList.contains('rmp-overlay-level-active')) {
                if (i === ccButtons.length - 1) {
                  nextIndexToActivate = 0;
                } else {
                  nextIndexToActivate = i + 1;
                }
                break;
              }
            }
            FW.createSyntheticEvent('click', ccButtons[nextIndexToActivate]);
          }
        }
        break;
      }
      case 'ArrowLeft': // left arrow = seek 5 sec back
        // when adUI is on - do not seek
        if (this.#rmp.hasAdUI) {
          break;
        }
        this.#seekToKeyboard(currentTime - 5000, currentTime, duration);
        break;
      case 'ArrowRight': // right arrow = seek 5 sec back
        // when adUI is on - do not seek
        if (this.#rmp.hasAdUI) {
          break;
        }
        this.#seekToKeyboard(currentTime + 5000, currentTime, duration);
        break;
      case 'ArrowUp': // up = volume +10%
        if (!this.#rmp.hideVolume) {
          if (currentVolume >= 0 || currentVolume <= 1) {
            if (currentVolume <= 0.9) {
              newVolume = currentVolume + 0.1;
            } else {
              newVolume = 1;
            }
            this.#rmp.volume = newVolume;
          }
        }
        break;
      case 'ArrowDown': // down = volume -10%
        if (!this.#rmp.hideVolume) {
          if (currentVolume >= 0 || currentVolume <= 1) {
            if (currentVolume >= 0.1) {
              newVolume = currentVolume - 0.1;
            } else {
              newVolume = 0;
            }
            this.#rmp.volume = newVolume;
          }
        }
        break;
      case 'm': // m = muted
        if (!this.#rmp.hideVolume) {
          if (this.#rmp.mute) {
            this.#rmp.mute = false;
          } else {
            this.#rmp.mute = true;
          }
        }
        break;
      case 'Escape': // esc key
        if (this.#rmp.dom.container.classList.contains('rmp-module-overlay-open')) {
          let className = this.#rmp.dom.container.className;
          if (className) {
            let regExp = /rmp-module-overlay-(quality|sharing|audio|captions|speed|related|chapters)/i;
            let type = className.match(regExp);
            if (type && type[1]) {
              this.#rmp.setModuleOverlayVisible(type[1], false);
            }
          }
        }
        break;
      case '0': // 0 = 0%
        this.#seekToKeyboard(0, currentTime, duration);
        break;
      case '1': // 1 = 10%
        this.#seekToKeyboard(duration * 0.1, currentTime, duration);
        break;
      case '2': // 2 = 20%
        this.#seekToKeyboard(duration * 0.2, currentTime, duration);
        break;
      case '3': // 3 = 30%
        this.#seekToKeyboard(duration * 0.3, currentTime, duration);
        break;
      case '4': // 4 = 40%
        this.#seekToKeyboard(duration * 0.4, currentTime, duration);
        break;
      case '5': // 5 = 50%
        this.#seekToKeyboard(duration * 0.5, currentTime, duration);
        break;
      case '6': // 6 = 60%
        this.#seekToKeyboard(duration * 0.6, currentTime, duration);
        break;
      case '7': // 7 = 70%
        this.#seekToKeyboard(duration * 0.7, currentTime, duration);
        break;
      case '8': // 8 = 80%
        this.#seekToKeyboard(duration * 0.8, currentTime, duration);
        break;
      case '9': // 9 = 90%
        this.#seekToKeyboard(duration * 0.9, currentTime, duration);
        break;
      default:
        break;
    }
  }

  button(element, ariaLabel, callClick, type, keyboardShortcut) {
    if (element) {
      this.#elementsFocus.push(element);
      if ((this.#rmp.dom.seekBar && element === this.#rmp.dom.seekBar) ||
        (this.#rmp.dom.volumeBar && element === this.#rmp.dom.volumeBar)) {
        element.setAttribute('role', 'slider');
        element.setAttribute('tabindex', '0');
        element.setAttribute('aria-valuemin', '0');
        element.setAttribute('aria-valuemax', '100');
      } else if (element === this.#rmp.dom.container) {
        element.setAttribute('tabindex', '0');
        if (this.#rmp.playerContainerRole !== '') {
          element.setAttribute('role', this.#rmp.playerContainerRole);
        }
      } else if (typeof element.nodeName === 'string' && element.nodeName.toLowerCase() === 'button') {
        element.setAttribute('type', 'button');
      } else if (type && type === 'playlist') {
        element.setAttribute('role', 'button');
        element.setAttribute('tabindex', '0');
        // prevent scrolling on playlist area
        if (!this.#rmp.disableKeyboardControl) {
          element.addEventListener('keydown', event => {
            if (event && typeof event.key === 'string' && event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              this.#playOrPause();
            }
          });
        }
      }
      if (ariaLabel) {
        element.setAttribute('aria-label', ariaLabel);
      }
      if (keyboardShortcut) {
        element.setAttribute('aria-keyshortcuts', keyboardShortcut);
      }
      if (!this.#rmp.disableKeyboardControl) {
        if (callClick) {
          element.addEventListener('keyup', event => {
            if (event && typeof event.key === 'string' && event.key === 'Enter') {
              if (this.#rmp.dom.cast && element === this.#rmp.dom.cast) {
                let icon = element.querySelector('google-cast-launcher');
                if (icon) {
                  FW.createSyntheticEvent('click', icon);
                }
              } else {
                FW.createSyntheticEvent('click', element);
                window.clearTimeout(this.#focusOnOpenOverlayTimeoutFn);
                this.#focusOnOpenOverlayTimeoutFn = window.setTimeout(() => {
                  const className = this.#rmp.dom.container.className;
                  const regex = /rmp-module-overlay-\w+/;
                  const found = className.match(regex);
                  if (found !== null) {
                    const words = found[0].split('-');
                    const type = words[3];
                    const elementWithFocus = this.#rmp.dom[type + 'Overlay'].querySelector('.rmp-overlay-level-active');
                    if (elementWithFocus) {
                      elementWithFocus.focus();
                      this.#addFocus(elementWithFocus);
                    }
                  }
                }, 400);
              }
            }
          });
        }
      }
      element.addEventListener('blur', () => {
        this.#removeFocus(element);
      });
    }
  }

  destroy() {
    window.clearTimeout(this.#focusOnOpenOverlayTimeoutFn);
    this.#rmp.dom.container.removeEventListener('keyup', this.#bindKeyboardFn);
    this.#rmp.dom.container.removeEventListener('blur', this.#removeFocusToContainerFn);
    this.#rmp.dom.container.removeEventListener('keydown', FW.preventKeyDown);
    document.removeEventListener('keyup', this.#addTabKeyDetectionFn);
  }

  init() {
    let ariaLabel = this.#rmp.contentMetadata.title;
    this.button(this.#rmp.dom.container, ariaLabel);

    if (this.#rmp.contentMetadata.description !== '') {
      this.#rmp.dom.container.setAttribute('aria-describedby', 'media-description-for-' + this.#rmp.id);
    }
    // wire tab navigation
    if (!this.#rmp.disableKeyboardControl) {
      this.#addTabKeyDetectionFn = this.#addTabKeyDetection.bind(this);
      document.addEventListener('keyup', this.#addTabKeyDetectionFn);
    }
    this.#removeFocusToContainerFn = this.#removeFocus.bind(this, this.#rmp.dom.container);
    this.#rmp.dom.container.addEventListener('blur', this.#removeFocusToContainerFn);
    // wire keyboard control
    if (!this.#rmp.disableKeyboardControl) {
      this.#bindKeyboardFn = this.#bindKeyboard.bind(this);
      this.#rmp.dom.container.addEventListener('keyup', this.#bindKeyboardFn);
      this.#rmp.dom.container.addEventListener('keydown', FW.preventKeyDown);
    }
    this.#rmp.dom.video.setAttribute('tabindex', '-1');
  }

}
